import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Search, User, FileText, ArrowRight, Loader, LayoutTemplate, Smile, CheckCircle2, Plus, X, Recycle, Upload, Download } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';

const TemplateCard = ({ template, selected, onSelect }) => {
  let details = {};
  try { details = JSON.parse(template.content); } catch (e) {}

  return (
    <div 
      onClick={() => onSelect(template.id)}
      className={`border rounded-xl p-4 cursor-pointer transition-all hover:shadow-md relative ${
        selected ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200' : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex justify-between items-start mb-2">
        <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-500 rounded-full">
          {template.category}
        </span>
        {selected && <CheckCircle2 className="text-purple-600 w-5 h-5" />}
      </div>
      <h3 className="font-bold text-gray-900 mb-1">{template.name}</h3>
      <p className="text-xs text-gray-500 line-clamp-2">{details.description}</p>
    </div>
  );
};

export default function NewProject() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('crawl'); // 'crawl' | 'reselect' | 'template'
  const [loading, setLoading] = useState(false);
  
  
  // Import Form State
  const [importResult, setImportResult] = useState(null);
  const [importData, setImportData] = useState({
    projectName: '',
    file: null
  });

  // Crawl Form State
  const [crawlData, setCrawlData] = useState({
    projectName: '',
    inputMode: 'basic',
    sourceType: 'keyword',
    crawlCount: 10,
    sortType: '100'
  });
  const [inputText, setInputText] = useState("");
  const [basicInputs, setBasicInputs] = useState(['', '', '']);
  const getParsedInputs = () => {
    if (crawlData.inputMode === 'basic') {
      return basicInputs.map(item => item.trim()).filter(item => item !== '');
    }
    return inputText.split(/\r?\n/).map(item => item.trim()).filter(item => item !== '');
  };

  // Reselect Form State (New)
  const [reselectData, setReselectData] = useState({
    projectName: '',
    sourceTaskId: '',
    inheritMode: 'selected'
  });
  const [tasks, setTasks] = useState([]); // List of existing tasks

  // Template Form State
  const [templateData, setTemplateData] = useState({
    projectName: '',
    templateId: null
  });
  const [templates, setTemplates] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('全部');

  const categories = ['全部', ...new Set(templates.map(t => t.category || '未分类'))];
  const filteredTemplates = selectedCategory === '全部' 
    ? templates 
    : templates.filter(t => (t.category || '未分类') === selectedCategory);

  // Fetch Templates
  useEffect(() => {
    if (activeTab === 'template' && templates.length === 0 && user) {
      axios.get(`${API_URL}/templates`, { headers: { 'x-user-email': user.email } })
        .then(res => setTemplates(res.data))
        .catch(err => console.error(err));
    }
  }, [activeTab, user]);

  // Fetch Tasks (For Reselect Mode)
  useEffect(() => {
    if (activeTab === 'reselect' && tasks.length === 0 && user) {
        axios.get(`${API_URL}/tasks`, { headers: { 'x-user-email': user.email } })
          .then(res => {
              // Only showing CRAWL_GENERATE type tasks that have content
              // 2026-03-06 Fix: allow GENERATED tasks too, as long as they have crawl data
              const validTasks = res.data.filter(t => 
                ((t.type === 'CRAWL_GENERATE' || t.type === 'RESELECT' || t.type === 'IMPORT')) && 
                (t.status === 'CONTENT_READY' || t.status === 'GENERATED' || t.status === 'COMPLETED')
              );
              setTasks(validTasks);
          })
          .catch(err => console.error(err));
    }
  }, [activeTab, user]);

  // Handle Navigation State
  useEffect(() => {
    if (location.state?.templateId) {
      setActiveTab('template');
      setTemplateData(prev => ({
        ...prev,
        templateId: location.state.templateId,
        projectName: location.state.templateName ? `${location.state.templateName} - 新项目` : prev.projectName
      }));
    }
  }, [location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (activeTab === 'template' && !templateData.templateId) {
      alert("请选择一个模板");
      return;
    }
    if (activeTab === 'reselect' && !reselectData.sourceTaskId) {
        alert("请选择一个来源任务");
        return;
    }

    setLoading(true);
    try {
      let payload = {};
      
      
      if (activeTab === 'import') {
        if (!importData.file) {
            alert("请选择一个Excel文件");
            setLoading(false);
            return;
        }
        const formData = new FormData();
        formData.append('projectName', importData.projectName);
        formData.append('file', importData.file);
        
        const res = await axios.post(`${API_URL}/projects/import`, formData, {
            headers: { 'x-user-email': user.email, 'Content-Type': 'multipart/form-data' }
        });
        
        // Show result dialog instead of immediate navigation
        setImportResult(res.data);
        return;
      }

      if (activeTab === 'crawl') {

        let rawParsed = getParsedInputs();
        
        // 1. Length validation (Max 30 chars per keyword)
        const tooLong = rawParsed.find(item => item.length > 30);
        if (tooLong) {
            alert(`${crawlData.sourceType === 'keyword' ? '关键词' : '账号'}太长（最大30字符）：\n"${tooLong.substring(0, 15)}..."\n请检查是否误复制了长文本。`);
            setLoading(false);
            return;
        }

        // 2. Invalid character validation (Must contain Chinese, letter, or number)
        const invalidPattern = rawParsed.find(item => !/[a-zA-Z0-9\u4e00-\u9fa5]/.test(item));
        if (invalidPattern) {
            alert(`无效的${crawlData.sourceType === 'keyword' ? '关键词' : '账号'}："${invalidPattern}"\n必须包含中文、字母或数字。`);
            setLoading(false);
            return;
        }

        // 3. Case-insensitive deduplication
        const uniqueSet = new Set();
        const deduplicated = [];
        let hasDuplicates = false;
        
        rawParsed.forEach(item => {
            const lowerItem = item.toLowerCase();
            if (!uniqueSet.has(lowerItem)) {
                uniqueSet.add(lowerItem);
                deduplicated.push(item);
            } else {
                hasDuplicates = true;
            }
        });

        if (deduplicated.length === 0) {
            alert("请输入至少一个有效的关键词或账号。");
            setLoading(false);
            return;
        }

        if (deduplicated.length > 20) {
            alert("最多只能输入20个" + (crawlData.sourceType === 'keyword' ? '关键词' : '小红书号'));
            setLoading(false);
            return;
        }
        
        if (hasDuplicates) {
            // Optional: You could show a toast, but an alert might block the flow. 
            // Since the user is OK with "auto filtering", let's just silently filter or show a non-blocking toast. 
            // We'll use alert for simplicity as they click submit.
            // Actually, alert is fine.
            alert("检测到重复输入，系统已自动为您过滤重复项，继续提交。");
        }

        payload = { 
            mode: 'crawl', 
            ...crawlData,
            sourceValue: deduplicated.join('\n') 
        };
      } else if (activeTab === 'reselect') {
          payload = {
              mode: 'reselect',
              projectName: reselectData.projectName,
              sourceTaskId: reselectData.sourceTaskId,
              inheritMode: reselectData.inheritMode
          };
      } else {
          payload = { mode: 'template', ...templateData };
      }

      const res = await axios.post(`${API_URL}/projects`, payload, {
        headers: { 'x-user-email': user.email }
      });
      
      if (activeTab === 'template') {
        navigate(`/tasks/${res.data.id}/config`);
      } else if (activeTab === 'reselect') {
        navigate(`/tasks/${res.data.id}/processing`); // Go to seed selection (Step 2)
      } else {
        navigate(`/tasks/${res.data.id}/processing`); // Go to crawling wait (Step 2)
      }
    } catch (err) {
      console.error("Failed to create project", err);
      alert(err.response?.data?.error || "创建失败");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto pb-20">
      <div className="mb-8 text-center">
        <h1 className="text-2xl font-bold text-gray-900">新建内容项目</h1>
        <p className="text-gray-500 mt-2">选择一种方式开始创作</p>
      </div>

      {/* Tabs */}
      <div className="flex justify-center mb-8">
        <div className="bg-gray-100 p-1 rounded-xl inline-flex gap-1">
          <button 
            onClick={() => setActiveTab('crawl')}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center ${
              activeTab === 'crawl' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            <Search className="w-4 h-4 mr-2" />
            智能抓取分析
          </button>
          
          <button 
            onClick={() => setActiveTab('reselect')}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center ${
              activeTab === 'reselect' ? 'bg-white text-green-600 shadow-sm' : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            <Recycle className="w-4 h-4 mr-2" />
            重选种子文章
          </button>

          <button 
            onClick={() => setActiveTab('template')}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center ${
              activeTab === 'template' ? 'bg-white text-purple-600 shadow-sm' : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            <LayoutTemplate className="w-4 h-4 mr-2" />
            使用现有模板
          </button>

          <button 
            onClick={() => setActiveTab('import')}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center ${
              activeTab === 'import' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            <Upload className="w-4 h-4 mr-2" />
            表格导入
          </button>

        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <form onSubmit={handleSubmit} className="p-8 space-y-8">
          
          {/* Project Name (Shared) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">项目名称</label>
            <input
              type="text"
              required
              value={
                  activeTab === 'import' ? importData.projectName :
                  activeTab === 'crawl' ? crawlData.projectName : 
                  activeTab === 'reselect' ? reselectData.projectName :
                  templateData.projectName
              }
              onChange={(e) => {
                  if (activeTab === 'import') setImportData({...importData, projectName: e.target.value});
                  else if (activeTab === 'crawl') setCrawlData({...crawlData, projectName: e.target.value});
                  else if (activeTab === 'reselect') setReselectData({...reselectData, projectName: e.target.value});
                  else setTemplateData({...templateData, projectName: e.target.value});
              }}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              placeholder="例如：2026春季推广..."
            />
          </div>

          <hr className="border-gray-100" />

          {activeTab === 'crawl' && (
            /* Crawl Config */
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
               {/* Source Type Toggle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">抓取方式 (消耗额度)</label>
                <div className="flex space-x-4">
                  <label className={`flex-1 border rounded-xl p-4 cursor-pointer transition-all ${crawlData.sourceType === 'keyword' ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input 
                      type="radio" 
                      name="sourceType" 
                      value="keyword" 
                      checked={crawlData.sourceType === 'keyword'}
                      onChange={() => setCrawlData({...crawlData, sourceType: 'keyword'})}
                      className="sr-only"
                    />
                    <div className="flex items-center mb-2">
                      <Search className={`w-5 h-5 mr-2 ${crawlData.sourceType === 'keyword' ? 'text-blue-600' : 'text-gray-400'}`} />
                      <span className={`font-medium ${crawlData.sourceType === 'keyword' ? 'text-blue-700' : 'text-gray-900'}`}>关键词搜索</span>
                    </div>
                    <p className="text-xs text-gray-500">搜索关键词采集笔记</p>
                  </label>

                  <label className={`flex-1 border rounded-xl p-4 cursor-pointer transition-all ${crawlData.sourceType === 'author' ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input 
                      type="radio" 
                      name="sourceType" 
                      value="author" 
                      checked={crawlData.sourceType === 'author'}
                      onChange={() => setCrawlData({...crawlData, sourceType: 'author'})}
                      className="sr-only"
                    />
                    <div className="flex items-center mb-2">
                      <User className={`w-5 h-5 mr-2 ${crawlData.sourceType === 'author' ? 'text-blue-600' : 'text-gray-400'}`} />
                      <span className={`font-medium ${crawlData.sourceType === 'author' ? 'text-blue-700' : 'text-gray-900'}`}>达人主页</span>
                    </div>
                    <p className="text-xs text-gray-500">采集指定账号的笔记</p>
                  </label>
                </div>
              </div>

              {/* Input Value (Dual Mode) */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {crawlData.sourceType === 'keyword' ? '输入关键词 (最多20个)' : '输入达人小红书号 (最多20个)'}
                  </label>
                  <button 
                    type="button"
                    onClick={() => setCrawlData({...crawlData, inputMode: crawlData.inputMode === 'basic' ? 'advanced' : 'basic'})}
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                  >
                    {crawlData.inputMode === 'basic' ? '进阶：批量导入文本' : '基础：逐个输入框模式'}
                  </button>
                </div>
                
                {crawlData.inputMode === 'basic' ? (
                  <div className="space-y-3">
                    {basicInputs.map((val, idx) => (
                      <div key={idx} className="flex gap-2">
                        <input
                          type="text"
                          value={val}
                          onChange={(e) => {
                            const newInputs = [...basicInputs];
                            newInputs[idx] = e.target.value;
                            setBasicInputs(newInputs);
                          }}
                          className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                          placeholder={crawlData.sourceType === 'keyword' ? `关键词 ${idx + 1}` : `小红书号 ${idx + 1}`}
                        />
                        {basicInputs.length > 1 && (
                          <button
                            type="button"
                            onClick={() => setBasicInputs(basicInputs.filter((_, i) => i !== idx))}
                            className="p-2 text-gray-400 hover:text-red-500 transition"
                          >
                            <X size={20} />
                          </button>
                        )}
                      </div>
                    ))}
                    {basicInputs.length < 20 && (
                      <button
                        type="button"
                        onClick={() => setBasicInputs([...basicInputs, ''])}
                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center mt-2 px-2 py-1 rounded hover:bg-blue-50 transition"
                      >
                        <Plus size={16} className="mr-1" />
                        添加{crawlData.sourceType === 'keyword' ? '关键词' : '账号'}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3 animate-in fade-in duration-300">
                    <textarea
                      value={inputText}
                      onChange={(e) => setInputText(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                      rows={6}
                      placeholder={crawlData.sourceType === 'keyword' 
                        ? "在此批量输入关键词，可使用回车换行分隔\n例如：\nOOTD\n美妆分享\n护肤干货\n夏日穿搭\n时尚好物" 
                        : "在此批量输入小红书号，可使用回车换行分隔\n例如：\n123456789\n987654321\n111222333"}
                    ></textarea>
                    <div className="mt-3 text-xs text-amber-600 bg-amber-50 p-2.5 rounded-lg border border-amber-200 flex items-start">
                      <span className="mr-1">⚠️</span>
                      <span><strong>注意：</strong>系统已调整为仅支持【换行符（回车）】作为分隔符。您可以放心地在Prompt内使用逗号、顿号等标点符号，不会被强行拆分。</span>
                    </div>
                  </div>
                )}
                
                <p className={`text-xs mt-3 ${getParsedInputs().length > 20 ? 'text-red-500 font-semibold' : 'text-gray-500'}`}>
                  已输入 {getParsedInputs().length} / 20 个
                </p>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">抓取数量 (Max 50)</label>
                  <input
                    type="number"
                    min="10"
                    max="50"
                    step="10"
                    value={crawlData.crawlCount}
                    onChange={(e) => {
                        let val = e.target.value === '' ? '' : parseInt(e.target.value);
                        if (val !== '' && val > 50) val = 50;
                        setCrawlData({...crawlData, crawlCount: val});
                    }}
                    onBlur={(e) => {
                        let val = parseInt(e.target.value);
                        if (isNaN(val) || val < 10) val = 10;
                        setCrawlData({...crawlData, crawlCount: val});
                    }}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">排序方式</label>
                  <select
                    value={crawlData.sortType}
                    onChange={(e) => setCrawlData({...crawlData, sortType: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                  >
                    <option value="100">综合排序</option>
                    <option value="001">最新排序</option>
                    <option value="010">最多点赞</option>
                    <option value="002">最多评论</option>
                    <option value="003">最多收藏</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'reselect' && (
            /* Reselect Config */
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
                    <Recycle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                    <div>
                        <h4 className="font-bold text-green-800 text-sm">复用现有资源</h4>
                        <p className="text-xs text-green-700 mt-1">从历史项目中导入已抓取的笔记，无需消耗抓取额度，直接重新筛选种子文章。</p>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">选择来源任务</label>
                    <div className="relative">
                        <select 
                            value={reselectData.sourceTaskId}
                            onChange={(e) => {
                                const taskId = e.target.value;
                                const task = tasks.find(t => String(t.id) === String(taskId));
                                setReselectData({
                                    ...reselectData,
                                    sourceTaskId: taskId,
                                    projectName: task ? `${task.name} (副本)` : ''
                                });
                            }}
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 outline-none bg-white appearance-none"
                        >
                            <option value="">-- 请选择 --</option>
                            {tasks.map(task => (
                                <option key={task.id} value={task.id}>
                                    #{task.id} - {task.name} ({new Date(task.createdAt).toLocaleDateString()})
                                </option>
                            ))}
                        </select>
                        {/* Custom arrow if needed, but standard select is fine for MVP */}
                    </div>
                    {tasks.length === 0 && (
                        <p className="text-xs text-red-500 mt-2">暂无可用历史任务 (需为 CRAWL_GENERATE 类型且已完成抓取)</p>
                    )}
                </div>

                <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">数据继承范围</label>
                    <div className="flex space-x-4">
                      <label className={`flex-1 border rounded-xl p-4 cursor-pointer transition-all ${reselectData.inheritMode === 'selected' ? 'border-green-500 bg-green-50 ring-1 ring-green-500' : 'border-gray-200 hover:border-gray-300'}`}>
                        <input 
                          type="radio" 
                          name="inheritMode" 
                          value="selected" 
                          checked={reselectData.inheritMode === 'selected'}
                          onChange={() => setReselectData({...reselectData, inheritMode: 'selected'})}
                          className="sr-only"
                        />
                        <div className="flex items-center mb-2">
                          <CheckCircle2 className={`w-5 h-5 mr-2 ${reselectData.inheritMode === 'selected' ? 'text-green-600' : 'text-gray-400'}`} />
                          <span className={`font-medium ${reselectData.inheritMode === 'selected' ? 'text-green-700' : 'text-gray-900'}`}>仅继承精选种子 (推荐)</span>
                        </div>
                        <p className="text-xs text-gray-500">仅导入来源任务中已被勾选的优质文章</p>
                      </label>

                      <label className={`flex-1 border rounded-xl p-4 cursor-pointer transition-all ${reselectData.inheritMode === 'all' ? 'border-green-500 bg-green-50 ring-1 ring-green-500' : 'border-gray-200 hover:border-gray-300'}`}>
                        <input 
                          type="radio" 
                          name="inheritMode" 
                          value="all" 
                          checked={reselectData.inheritMode === 'all'}
                          onChange={() => setReselectData({...reselectData, inheritMode: 'all'})}
                          className="sr-only"
                        />
                        <div className="flex items-center mb-2">
                          <LayoutTemplate className={`w-5 h-5 mr-2 ${reselectData.inheritMode === 'all' ? 'text-green-600' : 'text-gray-400'}`} />
                          <span className={`font-medium ${reselectData.inheritMode === 'all' ? 'text-green-700' : 'text-gray-900'}`}>继承全部原始抓取</span>
                        </div>
                        <p className="text-xs text-gray-500">导入来源任务中抓取的所有文章，重新挑选</p>
                      </label>
                    </div>
                </div>
            </div>
          )}

          
          {activeTab === 'import' && (
            /* Import Config */
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-start gap-3">
                    <FileText className="text-orange-600 flex-shrink-0 mt-0.5" size={20} />
                    <div>
                        <h4 className="font-bold text-orange-800 text-sm">导入本地Excel数据</h4>
                        <p className="text-xs text-orange-700 mt-1 mb-2">下载统一的模板，填写对应字段后上传即可直接进行种子筛选。</p>
                        <a 
                            href={`${API_URL}/projects/import-template`} 
                            download
                            className="inline-flex items-center space-x-1 text-xs font-medium text-orange-700 hover:text-orange-900 bg-white border border-orange-200 px-3 py-1.5 rounded-lg shadow-sm hover:shadow"
                        >
                            <Download size={14} />
                            <span>下载模板文件 (.xlsx)</span>
                        </a>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">上传填写好的模板文件</label>
                    <div 
                        className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:bg-gray-50 transition-colors"
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                            e.preventDefault();
                            if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                                setImportData({...importData, file: e.dataTransfer.files[0]});
                            }
                        }}
                    >
                        <input 
                            type="file" 
                            accept=".xlsx, .xls, .csv"
                            id="file-upload"
                            className="hidden"
                            onChange={(e) => setImportData({...importData, file: e.target.files[0]})}
                        />
                        <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center">
                            <Upload className="w-10 h-10 text-gray-400 mb-3" />
                            <span className="text-sm font-medium text-gray-700">点击选择或拖拽文件到这里上传</span>
                            <span className="text-xs text-gray-500 mt-1">支持 .xlsx, .xls, .csv 格式</span>
                        </label>
                        {importData.file && (
                            <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-lg inline-flex items-center text-sm text-blue-700">
                                <FileText className="w-4 h-4 mr-2" />
                                {importData.file.name}
                                <button 
                                    type="button" 
                                    onClick={(e) => { e.preventDefault(); setImportData({...importData, file: null}); }} 
                                    className="ml-3 text-blue-400 hover:text-blue-600"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
          )}

          {activeTab === 'template' && (
            /* Template Selection */
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">选择生成模板 (无需抓取，不扣额度)</label>
                {templates.length === 0 ? (
                    <div className="text-center py-10 bg-gray-50 rounded-xl text-gray-500">
                        暂无可用模板，请先到模板库创建。
                    </div>
                ) : (
                    <>
                        <div className="flex flex-wrap gap-2 mb-4">
                            {categories.map(cat => (
                                <button
                                    key={cat}
                                    type="button"
                                    onClick={(e) => { e.preventDefault(); setSelectedCategory(cat); }}
                                    className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${
                                        selectedCategory === cat 
                                            ? 'bg-purple-100 text-purple-700 border border-purple-200' 
                                            : 'bg-gray-100 text-gray-600 border border-transparent hover:bg-gray-200'
                                    }`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {filteredTemplates.map(t => (
                                <TemplateCard 
                                    key={t.id} 
                                    template={t} 
                                    selected={templateData.templateId === t.id}
                                    onSelect={(id) => setTemplateData({...templateData, templateId: id})}
                                />
                            ))}
                        </div>
                    </>
                )}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="pt-6 border-t border-gray-100 flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className={`px-8 py-3 rounded-xl font-medium transition shadow-lg flex items-center disabled:opacity-70 disabled:cursor-not-allowed text-white ${
                  activeTab === 'import' ? 'bg-orange-600 hover:bg-orange-700 shadow-orange-500/30' :
                  activeTab === 'template' ? 'bg-purple-600 hover:bg-purple-700 shadow-purple-500/30' : 
                  activeTab === 'reselect' ? 'bg-green-600 hover:bg-green-700 shadow-green-500/30' :
                  'bg-blue-600 hover:bg-blue-700 shadow-blue-500/30'
              }`}
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 mr-2 animate-spin" />
                  处理中...
                </>
              ) : (
                <>
                  {activeTab === 'import' ? '确认导入并筛选素材' :
                   activeTab === 'template' ? '直接开始配置' : 
                   activeTab === 'reselect' ? '确认并筛选素材' :
                   '提交抓取任务'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </button>
          </div>

        </form>
      </div>

      {/* Import Result Dialog */}
      {importResult && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <h2 className="text-xl font-bold text-gray-900 flex items-center">
                <CheckCircle2 className="w-6 h-6 text-green-500 mr-2" />
                导入结果报告
              </h2>
              <button onClick={() => navigate(`/tasks/${importResult.task.id}/processing`)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              <div className="flex gap-4 mb-6">
                <div className="flex-1 bg-green-50 rounded-xl p-4 border border-green-100">
                  <div className="text-sm text-green-600 mb-1">成功导入</div>
                  <div className="text-2xl font-bold text-green-700">{importResult.successCount} 条</div>
                </div>
                <div className="flex-1 bg-red-50 rounded-xl p-4 border border-red-100">
                  <div className="text-sm text-red-600 mb-1">失败/跳过</div>
                  <div className="text-2xl font-bold text-red-700">{importResult.failedCount} 条</div>
                </div>
              </div>

              {importResult.failedRows && importResult.failedRows.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-gray-900 mb-3 flex justify-between items-center">
                    失败明细
                  </h3>
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm text-left">
                      <thead className="bg-gray-50 text-gray-600 border-b border-gray-200">
                        <tr>
                          <th className="py-2 px-4 w-20">Excel行号</th>
                          <th className="py-2 px-4">失败原因</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {importResult.failedRows.map((row, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="py-2 px-4 font-medium text-gray-900">第 {row.rowNum} 行</td>
                            <td className="py-2 px-4 text-red-600">{row.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
              {importResult.failedRows && importResult.failedRows.length > 0 && (
                <button 
                  onClick={() => {
                    // Simple CSV download for failed rows
                    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" 
                      + "Excel行号,失败原因\n" 
                      + importResult.failedRows.map(e => `第${e.rowNum}行,"${e.reason}"`).join("\n");
                    const encodedUri = encodeURI(csvContent);
                    const link = document.createElement("a");
                    link.setAttribute("href", encodedUri);
                    link.setAttribute("download", "导入失败报告.csv");
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  className="px-5 py-2.5 rounded-xl font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition"
                >
                  下载失败报告
                </button>
              )}
              <button 
                onClick={() => navigate(`/tasks/${importResult.task.id}/processing`)}
                className="px-5 py-2.5 rounded-xl font-medium text-white bg-blue-600 hover:bg-blue-700 shadow-sm shadow-blue-500/30 transition flex items-center"
              >
                进入种子筛选
                <ArrowRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}